import React, {useEffect, useRef, useState} from "react";
import styled from "styled-components";

const lucideIcons = require("lucide-react/dist/cjs/lucide-react.cjs");

const SvgIcon = ({children, size = 18}) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        {children}
    </svg>
);

const CheckIcon = ({size}) => (
    <SvgIcon size={size}>
        <path d="M20 6 9 17l-5-5"/>
    </SvgIcon>
);

const ChevronDownIcon = ({size}) => (
    <SvgIcon size={size}>
        <path d="m6 9 6 6 6-6"/>
    </SvgIcon>
);

const getLucideIcon = (name, fallback) => lucideIcons[name] || lucideIcons.default?.[name] || fallback;
const Check = getLucideIcon('Check', CheckIcon);
const ChevronDown = getLucideIcon('ChevronDown', ChevronDownIcon);

const SelectWrapper = styled.div`
    position: relative;
    min-width: ${props => props.width || '190px'};
`;

const SelectButton = styled.button`
    width: 100%;
    min-height: 42px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 12px;
    border: 1px solid ${props => props.isOpen ? '#832729' : '#d6d6d6'};
    border-radius: 8px;
    color: #1f2933;
    background: #ffffff;
    box-shadow: ${props => props.isOpen ? '0 0 0 3px rgba(131, 39, 41, 0.12)' : '0 1px 2px rgba(15, 23, 42, 0.06)'};
    font-size: 14px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
`;

const Label = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Menu = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    z-index: 20;
    padding: 6px;
    border: 1px solid #e2e5ea;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.16);
`;

const Option = styled.button`
    width: 100%;
    min-height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 10px;
    border: 0;
    border-radius: 6px;
    color: ${props => props.isSelected ? '#832729' : '#344054'};
    background: ${props => props.isSelected ? '#fff2f2' : '#ffffff'};
    font-size: 14px;
    font-weight: ${props => props.isSelected ? 600 : 500};
    text-align: left;
    cursor: pointer;

    &:hover {
        background: #f7f7f8;
    }
`;

const MaterialSelect = ({options = [], value, onChange, width}) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef(null);
    const selectedOption = options.find((option) => option.value === value) || options[0];

    useEffect(() => {
        const onDocumentClick = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', onDocumentClick);
        return () => document.removeEventListener('mousedown', onDocumentClick);
    }, []);

    const onOptionClick = (nextValue) => {
        onChange(nextValue);
        setIsOpen(false);
    };

    return (
        <SelectWrapper ref={selectRef} width={width}>
            <SelectButton type="button" isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
                <Label>{selectedOption?.label}</Label>
                <ChevronDown size={18}/>
            </SelectButton>
            {isOpen && <Menu>
                {options.map((option) => (
                    <Option
                        key={option.value}
                        type="button"
                        isSelected={option.value === value}
                        onClick={() => onOptionClick(option.value)}
                    >
                        <Label>{option.label}</Label>
                        {option.value === value && <Check size={16}/>}
                    </Option>
                ))}
            </Menu>}
        </SelectWrapper>
    );
};

export default MaterialSelect;
